import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, HostListener, ViewChild, ElementRef } from '@angular/core';
import { ModalController, ToastController } from '@ionic/angular';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs/operators';
import { BaseClass } from '../../classes/base';
import { TranslationService } from '../../services/translation.service';
import { JobParams } from '../job-follow-up/models/jobs';
import { EmailRecipient } from './models/email-recipient.interface';
import { ReportEmailService } from 'src/app/shared/modals/email-send-modal/report-email.service';

@Component({
    selector: 'app-email-send-modal',
    templateUrl: './email-send-modal.component.html',
    styleUrls: ['./email-send-modal.component.scss']
})
export class EmailSendModalComponent extends BaseClass implements OnInit, OnDestroy {
    @Input() job: JobParams;
    @Input() isStandalone: boolean = false;
    @Output() goBackRequested = new EventEmitter<void>();
    @Output() emailSentSuccess = new EventEmitter<void>();
    @Output() closeRequested = new EventEmitter<void>();
    @ViewChild('searchContainer', { read: ElementRef }) searchContainer: ElementRef;


    public searchQuery: string = '';
    public searchResults: EmailRecipient[] = [];
    public selectedRecipients: EmailRecipient[] = [];
    public isSearching: boolean = false;
    public showResults: boolean = false;
    public currentPage: number = 1;
    public totalPages: number = 0;
    public hasMoreResults: boolean = false;
    public isLoadingMore: boolean = false;

    private _searchSubject: Subject<string> = new Subject();
    private _searchSubscription: Subscription;
    private readonly PAGE_SIZE = 20;

    constructor(
        protected _toastController: ToastController,
        protected _translationService: TranslationService,
        private _modalController: ModalController,
        private _reportEmailService: ReportEmailService
    ) {
        super(_toastController, _translationService);
    }

    ngOnInit() {
        this._setupSearchSubscription();
    }

    ngOnDestroy() {
        if (this._searchSubscription) {
            this._searchSubscription.unsubscribe();
        }
    }
    
    @HostListener('click', ['$event'])
    public onClickOutside(event: MouseEvent): void {
        if (this.searchContainer && !this.searchContainer.nativeElement.contains(event.target)) {
            this.showResults = false;
        }
    }
    

    private _setupSearchSubscription(): void {
        this._searchSubscription = this._searchSubject.pipe(
            debounceTime(1000),
            distinctUntilChanged(),
            filter(query => query.length >= 3)
        ).subscribe(query => {
            this.isSearching = true;
            this.currentPage = 1;

            this._reportEmailService.searchUsers(query, this.currentPage, this.PAGE_SIZE).subscribe(
                response => {
                    this.searchResults = response.page.filter(user =>
                        !this.selectedRecipients.find(selected => selected.id === user.id)
                    );
                    this.totalPages = response.pageCount;
                    this.hasMoreResults = this.currentPage < this.totalPages;
                    this.isSearching = false;
                    this.showResults = this.searchResults.length > 0;
                },
                error => {
                    this.isSearching = false;
                    this.showResults = false;
                    this.hasMoreResults = false;
                    this.notifyError(error);
                }
            );
        });
    }

    public onSearchChange(event: any): void {
        const query = event.target.value || '';
        this.searchQuery = query;

        if (query.length < 3) {
            this.searchResults = [];
            this.showResults = false;
            this.isSearching = false;
            return;
        }

        this._searchSubject.next(query);
    }

    public selectUser(user: EmailRecipient): void {
        const alreadySelected = this.selectedRecipients.find(r => r.id === user.id);

        if (!alreadySelected) {
            this.selectedRecipients.push(user);
            this.searchResults = this.searchResults.filter(r => r.id !== user.id);

            // Limpar busca após seleção
            this.searchQuery = '';
            this.showResults = false;
        }
    }

    public loadMoreResults(): void {
        if (!this.hasMoreResults || this.isLoadingMore || !this.searchQuery) {
            return;
        }

        this.isLoadingMore = true;
        this.currentPage++;

        this._reportEmailService.searchUsers(this.searchQuery, this.currentPage, this.PAGE_SIZE).subscribe(
            response => {
                const newUsers = response.page.filter(user =>
                    !this.selectedRecipients.find(selected => selected.id === user.id)
                );
                this.searchResults = [...this.searchResults, ...newUsers];
                this.totalPages = response.pageCount;
                this.hasMoreResults = this.currentPage < this.totalPages;
                this.isLoadingMore = false;
            },
            error => {
                this.isLoadingMore = false;
                this.notifyError(error);
            }
        );
    }

    public removeRecipient(userId: string): void {
        this.selectedRecipients = this.selectedRecipients.filter(r => r.id !== userId);
    }

    public get recipientsCount(): number {
        return this.selectedRecipients.length;
    }

    public get recipientsCountText(): string {
        const count = this.recipientsCount;
        const translationKey = count === 1 
            ? 'emailSendModal.recipientSelectedSingular' 
            : 'emailSendModal.recipientSelectedPlural';
        const template = this._translationService.getTranslation(translationKey);
        return template.replace('{count}', count.toString());
    }

    public async sendEmails(): Promise<void> {
        if (this.selectedRecipients.length === 0) {
            this.notify(
                this._translationService.getTranslation('emailSendModal.noRecipientsSelected'), 
                'warning', 
                3000
            );
            return;
        }

        const userIds = this.selectedRecipients.map(r => r.id);

        this._reportEmailService.sendReportByEmail(this.job.jobId, this.job.jobName, userIds).subscribe(
            response => {
                if (response.success) {
                    this.notifySuccess('Report.sendReportLinkByEmail');
                    if (this.isStandalone) {
                        this.emailSentSuccess.emit();
                    } else {
                        this.dismiss(true);
                    }
                } else {
                    this.notifyError(
                        this._translationService.getTranslation('emailSendModal.sendError'),
                        true
                    );
                }
            },
            error => {
                this.notifyError(error);
            }
        );
    }

    public goBack(): void {
        if (this.isStandalone) {
            this.goBackRequested.emit();
        } else {
            this.dismiss(false);
        }
    }

    public close(): void {
        if (this.isStandalone) {
            this.closeRequested.emit();
        } else {
            this.dismiss(false);
        }
    }

    private async dismiss(data?: any): Promise<void> {
        await this._modalController.dismiss(data);
    }
}
